/**
 * The phone app's agreement to the Terms of Service and the Community
 * Guidelines (App Review 1.2; docs/mobile/store-compliance.md S1, S2):
 * sign-up asks for it, with the zero-tolerance line, and an account that
 * signed up elsewhere agrees once here before the app opens. The server
 * keeps no record of it, so this device remembers it per account.
 */
import type { Snowflake } from '@neosocial/protocol';
import { create } from 'zustand';
import { useT } from '../i18n';
import { isMobileApp } from '../lib/config';
import { storage } from '../lib/storage';
import { useData } from '../store/data';
import { signOut } from '../store/session';
import { Btn } from '../ui/kit';
import { Dialog } from '../ui/overlay';

export const TERMS_URL = 'https://neoxify.app/terms';
export const GUIDELINES_URL = 'https://neoxify.app/guidelines';
export const PRIVACY_URL = 'https://neoxify.app/privacy';

const key = (userId: Snowflake) => `terms-accepted:${userId}`;

/** Accounts that agreed since the app started (the storage holds the rest). */
export const useTerms = create<{ accepted: Record<Snowflake, true> }>(() => ({ accepted: {} }));

export function termsAccepted(userId: Snowflake): boolean {
  return !!useTerms.getState().accepted[userId] || !!storage.get(key(userId));
}

export function acceptTerms(userId: Snowflake): void {
  storage.set(key(userId), new Date().toISOString());
  useTerms.setState((s) => ({ accepted: { ...s.accepted, [userId]: true } }));
}

/**
 * "I agree to the Terms of Service and the Community Guidelines, and have
 * read the Privacy Policy", the three pages linked (the privacy policy is
 * reachable before an account exists, as the stores ask).
 */
export function TermsAgreement({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange(v: boolean): void;
}) {
  const { t, tRich } = useT();
  const link = (href: string, label: string) => (
    <a className="ns-link" href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
  return (
    <div className="ns-field ns-terms">
      <label className="ns-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>
          {tRich('mob.terms.agree', {
            terms: link(TERMS_URL, t('mob.terms.terms')),
            guidelines: link(GUIDELINES_URL, t('mob.terms.guidelines')),
            privacy: link(PRIVACY_URL, t('mob.terms.privacy')),
          })}
        </span>
      </label>
      <span className="ns-help">{t('mob.terms.zero')}</span>
    </div>
  );
}

/**
 * The phone app, signed in to an account that has not agreed on this device
 * (it signed up on the web, or before the agreement): the agreement first,
 * not skippable; Sign out is the way out.
 */
export function TermsGate() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const accepted = useTerms((s) => (me ? !!s.accepted[me.id] : false));
  if (!isMobileApp() || !me || accepted || termsAccepted(me.id)) return null;
  return (
    <Dialog
      title={t('mob.terms.title')}
      icon="shield"
      tone="mode"
      dismissable={false}
      onClose={() => {}}
      width={460}
      cls="ns-terms-gate"
    >
      <p>{t('mob.terms.body', { brand: t('ns.brand') })}</p>
      <p className="ns-help">{t('mob.terms.zero')}</p>
      <p className="ns-auth-links">
        <a className="ns-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">
          {t('mob.terms.terms')}
        </a>{' '}
        <a className="ns-link" href={GUIDELINES_URL} target="_blank" rel="noopener noreferrer">
          {t('mob.terms.guidelines')}
        </a>{' '}
        <a className="ns-link" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          {t('mob.terms.privacy')}
        </a>
      </p>
      <div className="ns-row-btns end">
        <Btn size="sm" kind="quiet" icon="power" onClick={() => void signOut()}>
          {t('ns.signout')}
        </Btn>
        <Btn kind="primary" size="sm" onClick={() => acceptTerms(me.id)} data-autofocus>
          {t('mob.terms.accept')}
        </Btn>
      </div>
    </Dialog>
  );
}
